import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import ReportPageHeader from "../components/reports/ReportPageHeader";
import InfoTooltip from "../components/common/InfoTooltip";
import SortableTh from "../components/common/SortableTh";
import ReportHelpCard from "../components/reports/ReportHelpCard";
import { useAuth } from "../context/AuthContext";
import useHelpGuideNavigation from "../hooks/useHelpGuideNavigation";
import {
  exportSalesTicketsReport,
  getSalesTicketsReport,
  getSalesTicketsTopTimeRankings,
} from "../api/reports";
import {
  BTN_OUTLINE,
  BTN_PRIMARY,
  FIELD_CLASS,
  LABEL_TEXT,
  MODAL_TEXT,
} from "../ui/modalStyles";
import { getSalonTimezone } from "../utils/salonTimezone";
import useTableSort from "../hooks/useTableSort";
import { SALES_AND_TICKETS_TOOLTIPS } from "../constants/reportTooltips";
import { REPORT_HELP } from "../constants/reportHelpContent";
import { getReportsHomePathForRoute } from "../utils/reportRouteContext";
import "./SalesTicketsTrendsReport.css";

const MANAGER_ROLES = [
  "admin",
  "host",
  "owner",
  "manager",
  "solo_technician",
  "reception",
];

const WEEKDAY_LABELS = [
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
  "Sunday",
];

const formatDateISO = (value, timeZone) => {
  if (!(value instanceof Date) || Number.isNaN(value.getTime())) return "";
  if (timeZone) {
    try {
      const formatter = new Intl.DateTimeFormat("en-CA", {
        timeZone,
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
      });
      return formatter.format(value);
    } catch (err) {
      // Fall back to local time when the timezone is invalid.
    }
  }
  const year = value.getFullYear();
  const month = String(value.getMonth() + 1).padStart(2, "0");
  const day = String(value.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const addDays = (base, days) => {
  const next = new Date(base.getTime());
  next.setDate(next.getDate() + days);
  return next;
};

const formatNumber = (value, digits = 0) => {
  const num = Number(value ?? 0);
  if (!Number.isFinite(num)) {
    return digits > 0 ? (0).toFixed(digits) : "0";
  }
  return num.toFixed(digits);
};

const formatPercent = (value) => {
  const num = Number(value ?? 0);
  if (!Number.isFinite(num)) return "0.0%";
  return `${num.toFixed(1)}%`;
};

const formatRatioPercent = (value) => {
  const num = Number(value ?? 0);
  if (!Number.isFinite(num)) return "0.0%";
  return `${(num * 100).toFixed(1)}%`;
};

const toTitleCase = (value) => {
  const text = String(value || "").trim();
  if (!text) return "Unknown";
  return text
    .split(/[\s_-]+/)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
};

const TICKET_VOLUME_COLUMNS = [
  {
    key: "period",
    label: "Period",
    tooltipKey: "column_period",
    type: "text",
  },
  {
    key: "closed_tickets",
    label: "Closed Tickets",
    tooltipKey: "column_closed_tickets",
    type: "number",
    render: (row) => formatNumber(row.closed_tickets, 0),
  },
  {
    key: "refunded_tickets",
    label: "Refunded Tickets",
    tooltipKey: "column_refunded_tickets",
    type: "number",
    render: (row) => formatNumber(row.refunded_tickets, 0),
  },
];

const TICKET_TYPE_COLUMNS = [
  {
    key: "type",
    label: "Ticket Type",
    tooltipKey: "column_ticket_type",
    type: "text",
    render: (row) => toTitleCase(row.type),
  },
  {
    key: "count",
    label: "Tickets",
    tooltipKey: "column_ticket_count",
    type: "number",
    render: (row) => formatNumber(row.count, 0),
  },
];

const CANCELLATION_COLUMNS = [
  {
    key: "period",
    label: "Period",
    tooltipKey: "column_period",
    type: "text",
  },
  {
    key: "cancelled",
    label: "Cancelled",
    tooltipKey: "column_cancelled",
    type: "number",
    render: (row) => formatNumber(row.cancelled, 0),
  },
  {
    key: "no_show",
    label: "No-Show",
    tooltipKey: "column_no_show",
    type: "number",
    render: (row) => formatNumber(row.no_show, 0),
  },
];

const CANCELLATION_TIMING_COLUMNS = [
  {
    key: "bucket",
    label: "Timing Bucket",
    tooltipKey: "column_timing_bucket",
    type: "text",
    render: (row) => {
      const bucket = row.bucket;
      if (bucket === "same_day") return "Same Day";
      if (bucket === "1_day") return "1 Day Before";
      if (bucket === "2_7_days") return "2-7 Days Before";
      if (bucket === "gt_7_days") return ">7 Days Before";
      return toTitleCase(bucket);
    },
  },
  {
    key: "count",
    label: "Cancelled",
    tooltipKey: "column_cancelled",
    type: "number",
    render: (row) => formatNumber(row.count, 0),
  },
];

const SOURCE_COLUMNS = [
  {
    key: "source",
    label: "Source",
    tooltipKey: "column_booking_source",
    type: "text",
    render: (row) => toTitleCase(row.source),
  },
  {
    key: "count",
    label: "Appointments",
    tooltipKey: "column_appointments",
    type: "number",
    render: (row) => formatNumber(row.count, 0),
  },
];

const CONVERSION_COLUMNS = [
  {
    key: "period",
    label: "Period",
    tooltipKey: "column_period",
    type: "text",
  },
  {
    key: "appointments_total",
    label: "Appointments",
    tooltipKey: "column_appointments",
    type: "number",
    render: (row) => formatNumber(row.appointments_total, 0),
  },
  {
    key: "appointments_served",
    label: "Served",
    tooltipKey: "column_served",
    type: "number",
    render: (row) => formatNumber(row.appointments_served, 0),
  },
  {
    key: "appointments_with_closed_ticket",
    label: "With Closed Ticket",
    tooltipKey: "column_with_closed_ticket",
    type: "number",
    render: (row) => formatNumber(row.appointments_with_closed_ticket, 0),
  },
  {
    key: "conversion_rate_pct",
    label: "Conversion %",
    tooltipKey: "column_conversion_rate",
    type: "percent",
    render: (row) => formatPercent(row.conversion_rate_pct),
  },
];

const RANKING_COLUMNS = [
  {
    key: "rank",
    label: "Rank",
    tooltipKey: null,
    type: "number",
    render: (row) => formatNumber(row.rank, 0),
  },
  {
    key: "label",
    label: "Bucket",
    tooltipKey: null,
    type: "text",
  },
  {
    key: "revenue",
    label: "Revenue",
    tooltipKey: null,
    type: "number",
    render: (row) => formatNumber(row.revenue, 2),
  },
  {
    key: "tickets",
    label: "Tickets",
    tooltipKey: null,
    type: "number",
    render: (row) => formatNumber(row.tickets, 0),
  },
  {
    key: "pct",
    label: "% Total",
    tooltipKey: null,
    type: "percent",
    render: (row) => formatRatioPercent(row.pct),
  },
];

function SectionHeading({ label, tooltip }) {
  return (
    <div className="flex items-center gap-2 text-base md:text-lg font-semibold text-slate-900">
      <span>{label}</span>
      {tooltip ? <InfoTooltip text={tooltip} label={label} /> : null}
    </div>
  );
}

function ReportTable({ columns, rows, rowKey, loading, emptyMessage }) {
  const safeRows = Array.isArray(rows) ? rows : [];
  const columnMap = useMemo(() => {
    const map = {};
    columns.forEach((col) => {
      map[col.key] = {
        type: col.type || "text",
        getValue: col.getValue || ((row) => row?.[col.key]),
      };
    });
    return map;
  }, [columns]);

  const tableSort = useTableSort({
    rows: safeRows,
    columns: columnMap,
    fallbackKey: columns[0]?.key,
  });

  return (
    <div className="rounded-2xl border border-slate-200 bg-white shadow-sm overflow-hidden">
      <div className="overflow-x-auto">
        <table className="min-w-full text-sm">
          <thead className="bg-slate-50 text-slate-600">
            <tr>
              {columns.map((col) => (
                <SortableTh
                  key={col.key}
                  label={col.label}
                  sortKey={col.key}
                  sortState={tableSort.sortState}
                  onSort={tableSort.handleSort}
                  tooltip={SALES_AND_TICKETS_TOOLTIPS[col.tooltipKey]}
                  className="px-4 py-3 text-center"
                  align="center"
                />
              ))}
            </tr>
          </thead>
          <tbody>
            {tableSort.sortedRows.map((row, index) => {
              const keyValue =
                (typeof rowKey === "function" ? rowKey(row, index) : row?.[rowKey]) ?? index;
              return (
                <tr key={keyValue} className="border-t border-slate-100">
                  {columns.map((col) => (
                    <td key={`${keyValue}-${col.key}`} className="px-4 py-3 text-center text-slate-700">
                      {col.render ? col.render(row) : row?.[col.key] ?? "--"}
                    </td>
                  ))}
                </tr>
              );
            })}
            {!tableSort.sortedRows.length && (
              <tr>
                <td colSpan={columns.length} className="px-4 py-8 text-center text-slate-500">
                  {loading ? "Loading..." : emptyMessage || "No results for this range."}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default function SalesTicketsTrendsReport() {
  const navigate = useNavigate();
  const location = useLocation();
  const { token, role, user, currentSalon, loading: authLoading } = useAuth();
  const { jumpToSectionWithHighlight, getSectionClassName } = useHelpGuideNavigation();
  const helpGuide = REPORT_HELP.SALES_TICKETS;

  const normalizedRole = (role || user?.role || "").toLowerCase();
  const hasManagerRole =
    MANAGER_ROLES.includes(normalizedRole) || Boolean(user?.is_super_admin);

  const resolvedSalonId = useMemo(() => {
    const raw = currentSalon?.id ?? currentSalon?.salon_id ?? user?.salon_id ?? null;
    if (raw === null || raw === undefined || raw === "") return null;
    const asNum = Number(raw);
    return Number.isFinite(asNum) ? asNum : null;
  }, [currentSalon, user?.salon_id]);

  const salonTimezone = useMemo(() => getSalonTimezone(currentSalon), [currentSalon]);
  const today = useMemo(() => formatDateISO(new Date(), salonTimezone), [salonTimezone]);
  const defaultStart = useMemo(
    () => formatDateISO(addDays(new Date(), -6), salonTimezone),
    [salonTimezone]
  );

  const [startDate, setStartDate] = useState(defaultStart);
  const [endDate, setEndDate] = useState(today);
  const [groupBy, setGroupBy] = useState("day");
  const [heatmapBucket, setHeatmapBucket] = useState(60);

  const renderFieldLabel = (label, tooltipKey) => (
    <div className="flex items-center gap-1.5">
      <label className={LABEL_TEXT}>{label}</label>
      {tooltipKey ? (
        <InfoTooltip text={SALES_AND_TICKETS_TOOLTIPS[tooltipKey]} label={label} />
      ) : null}
    </div>
  );

  const [ticketVolume, setTicketVolume] = useState([]);
  const [ticketTypeBreakdown, setTicketTypeBreakdown] = useState([]);
  const [cancellations, setCancellations] = useState([]);
  const [cancellationTiming, setCancellationTiming] = useState([]);
  const [bookingSources, setBookingSources] = useState([]);
  const [peakHeatmap, setPeakHeatmap] = useState([]);
  const [visitToSale, setVisitToSale] = useState([]);
  const [summary, setSummary] = useState(null);
  const [tzName, setTzName] = useState(salonTimezone);

  const [rankingType, setRankingType] = useState("day_of_week");
  const [rankingTopN, setRankingTopN] = useState(5);
  const [rankingRows, setRankingRows] = useState([]);
  const [rankingTotals, setRankingTotals] = useState(null);
  const [rankingLoading, setRankingLoading] = useState(false);
  const [rankingError, setRankingError] = useState("");

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const hasAutoRunRef = useRef(false);

  const dateRangeError = useMemo(() => {
    if (!startDate || !endDate) return "";
    return endDate < startDate ? "End date cannot be before start date." : "";
  }, [startDate, endDate]);

  const payloadBase = useMemo(
    () => ({
      start_date: startDate,
      end_date: endDate,
      group_by: groupBy,
      heatmap_bucket_minutes: Number(heatmapBucket) === 30 ? 30 : 60,
    }),
    [startDate, endDate, groupBy, heatmapBucket]
  );

  const rankingPayload = useMemo(
    () => ({
      start_date: startDate,
      end_date: endDate,
      rank_by: rankingType,
      top_n: Math.min(Math.max(Number(rankingTopN) || 5, 1), 50),
      bucket_minutes: Number(heatmapBucket) === 30 ? 30 : 60,
    }),
    [startDate, endDate, rankingType, rankingTopN, heatmapBucket]
  );

  useEffect(() => {
    hasAutoRunRef.current = false;
  }, [resolvedSalonId]);

  useEffect(() => {
    setRankingRows([]);
    setRankingTotals(null);
    setRankingError("");
  }, [startDate, endDate, rankingType, rankingTopN, heatmapBucket, resolvedSalonId]);

  useEffect(() => {
    if (authLoading) return;
    if (!token) {
      navigate("/login?next=/manager/reports/sales-tickets", { replace: true });
      return;
    }
    if (!hasManagerRole) {
      navigate("/my-bookings", { replace: true });
      return;
    }
    if (resolvedSalonId) return;
    setError("Select a salon to view reports.");
    const timeout = setTimeout(() => {
      navigate("/my-salons", { replace: true });
    }, 600);
    return () => clearTimeout(timeout);
  }, [authLoading, token, hasManagerRole, resolvedSalonId, navigate]);

  const fetchReport = useCallback(
    async (payloadOverride = null) => {
      if (authLoading) return;
      if (!resolvedSalonId) {
        setError(
          "Unable to determine your current salon. Please refresh or re-select your salon."
        );
        return;
      }
      if (dateRangeError) {
        setError(dateRangeError);
        return;
      }
      const requestPayload = payloadOverride || payloadBase;
      setLoading(true);
      setError("");
      try {
        const data = await getSalesTicketsReport(requestPayload);
        setTicketVolume(Array.isArray(data?.ticket_volume) ? data.ticket_volume : []);
        setTicketTypeBreakdown(
          Array.isArray(data?.ticket_type_breakdown) ? data.ticket_type_breakdown : []
        );
        setCancellations(Array.isArray(data?.cancellations) ? data.cancellations : []);
        setCancellationTiming(
          Array.isArray(data?.cancellation_timing) ? data.cancellation_timing : []
        );
        setBookingSources(Array.isArray(data?.booking_sources) ? data.booking_sources : []);
        setPeakHeatmap(Array.isArray(data?.peak_heatmap) ? data.peak_heatmap : []);
        setVisitToSale(Array.isArray(data?.visit_to_sale) ? data.visit_to_sale : []);
        setSummary(data?.summary || null);
        setTzName(data?.tz_name || salonTimezone);
      } catch (err) {
        setTicketVolume([]);
        setTicketTypeBreakdown([]);
        setCancellations([]);
        setCancellationTiming([]);
        setBookingSources([]);
        setPeakHeatmap([]);
        setVisitToSale([]);
        setSummary(null);
        setError(err?.response?.data?.detail || "Unable to load Sales & Tickets report.");
      } finally {
        setLoading(false);
      }
    },
    [authLoading, resolvedSalonId, dateRangeError, payloadBase, salonTimezone]
  );

  const fetchRanking = useCallback(async () => {
    if (authLoading) return;
    if (!resolvedSalonId) {
      setRankingError(
        "Unable to determine your current salon. Please refresh or re-select your salon."
      );
      return;
    }
    if (dateRangeError) {
      setRankingError(dateRangeError);
      return;
    }
    setRankingLoading(true);
    setRankingError("");
    try {
      const data = await getSalesTicketsTopTimeRankings(rankingPayload);
      setRankingRows(Array.isArray(data?.rows) ? data.rows : []);
      setRankingTotals(data?.totals || null);
    } catch (err) {
      setRankingRows([]);
      setRankingTotals(null);
      setRankingError("Couldn't load the ranking. Please try again.");
    } finally {
      setRankingLoading(false);
    }
  }, [authLoading, resolvedSalonId, dateRangeError, rankingPayload]);

  useEffect(() => {
    if (authLoading || !resolvedSalonId || hasAutoRunRef.current) return;
    hasAutoRunRef.current = true;
    fetchReport(payloadBase);
  }, [authLoading, resolvedSalonId, fetchReport, payloadBase]);

  const handleExport = async () => {
    if (dateRangeError) {
      setError(dateRangeError);
      return;
    }
    try {
      await exportSalesTicketsReport(payloadBase);
    } catch (err) {
      setError(err?.response?.data?.detail || "Unable to export Sales & Tickets data.");
    }
  };

  const tiles = useMemo(() => {
    const topSource = toTitleCase(summary?.top_booking_source);
    const topSourceCount = formatNumber(summary?.top_booking_source_count, 0);
    return [
      { label: "Closed Tickets", value: formatNumber(summary?.total_closed_tickets, 0) },
      { label: "Refunded Tickets", value: formatNumber(summary?.total_refunded_tickets, 0) },
      { label: "Conversion Rate", value: formatPercent(summary?.overall_conversion_rate_pct) },
      { label: "Cancelled Appts", value: formatNumber(summary?.total_cancelled_appointments, 0) },
      { label: "No-Shows", value: formatNumber(summary?.total_no_show_appointments, 0) },
      { label: "Top Source", value: `${topSource} (${topSourceCount})` },
    ];
  }, [summary]);

  const heatmapBucketMinutes = payloadBase.heatmap_bucket_minutes;
  const heatmapBucketCount = heatmapBucketMinutes === 30 ? 48 : 24;

  const heatmapLabels = useMemo(() => {
    const pad2 = (num) => String(num).padStart(2, "0");
    const labels = [];
    for (let index = 0; index < heatmapBucketCount; index += 1) {
      const hour = heatmapBucketMinutes === 30 ? Math.floor(index / 2) : index;
      const minute = heatmapBucketMinutes === 30 ? (index % 2) * 30 : 0;
      labels.push(`${pad2(hour)}:${pad2(minute)}`);
    }
    return labels;
  }, [heatmapBucketCount, heatmapBucketMinutes]);

  const heatmapColumns = useMemo(() => {
    const columns = [
      {
        key: "weekday_label",
        label: "Weekday",
        tooltipKey: "column_weekday",
        type: "number",
        getValue: (row) => row?.weekday,
      },
    ];
    for (let index = 0; index < heatmapBucketCount; index += 1) {
      const key = `b_${index}`;
      columns.push({
        key,
        label: heatmapLabels[index],
        tooltipKey: "column_heatmap_bucket",
        type: "number",
        render: (row) => formatNumber(row?.[key], 0),
      });
    }
    return columns;
  }, [heatmapBucketCount, heatmapLabels]);

  const heatmapRows = useMemo(() => {
    const countsByWeekday = Array.from({ length: 7 }, () =>
      Array.from({ length: heatmapBucketCount }, () => 0)
    );
    (Array.isArray(peakHeatmap) ? peakHeatmap : []).forEach((cell) => {
      const weekday = Number(cell?.weekday);
      const bucket = Number(cell?.hour_bucket);
      const count = Number(cell?.count ?? 0);
      if (
        Number.isFinite(weekday) &&
        Number.isFinite(bucket) &&
        weekday >= 0 &&
        weekday < 7 &&
        bucket >= 0 &&
        bucket < heatmapBucketCount
      ) {
        countsByWeekday[weekday][bucket] += Number.isFinite(count) ? count : 0;
      }
    });

    return WEEKDAY_LABELS.map((label, weekday) => {
      const row = { weekday, weekday_label: label };
      for (let index = 0; index < heatmapBucketCount; index += 1) {
        row[`b_${index}`] = countsByWeekday[weekday][index] ?? 0;
      }
      return row;
    });
  }, [peakHeatmap, heatmapBucketCount]);

  const handleClose = (event) => {
    event?.preventDefault?.();
    event?.stopPropagation?.();
    navigate(getReportsHomePathForRoute(location.pathname), { replace: true });
  };

  return (
    <div className="salesTicketsPageShell min-h-screen bg-slate-100">
      <div className="salesTicketsPageWide sales-tickets-page-compact">
        <div className="rounded-3xl bg-white shadow-2xl overflow-hidden">
          <ReportPageHeader title="Sales & Tickets" onClose={handleClose} />

          <div className={`px-6 py-6 space-y-6 ${MODAL_TEXT}`}>
            <div id="sales-tickets-overview" className={getSectionClassName("sales-tickets-overview")}>
              <ReportHelpCard
                title={helpGuide.title}
                subtitle={helpGuide.subtitle}
                sections={helpGuide.sections}
                troubleshooting={helpGuide.troubleshooting}
                storageKey="report_help_sales_tickets"
                onJumpToSection={jumpToSectionWithHighlight}
              />
            </div>
            {error && (
              <div className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-rose-700">
                {error}
              </div>
            )}

            <div
              id="sales-tickets-filters"
              className={`rounded-2xl border border-slate-200 bg-white shadow-sm p-5 space-y-4 ${getSectionClassName(
                "sales-tickets-filters"
              )}`}
            >
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
                <div>
                  {renderFieldLabel("Date From", "start_date")}
                  <input
                    type="date"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    className={`${FIELD_CLASS} w-full`}
                  />
                </div>
                <div>
                  {renderFieldLabel("Date To", "end_date")}
                  <input
                    type="date"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    className={`${FIELD_CLASS} w-full`}
                  />
                </div>
                <div>
                  {renderFieldLabel("Group By", "group_by")}
                  <select
                    value={groupBy}
                    onChange={(e) => setGroupBy(e.target.value)}
                    className={`${FIELD_CLASS} w-full`}
                  >
                    <option value="day">Day</option>
                    <option value="week">Week</option>
                  </select>
                </div>
                <div>
                  {renderFieldLabel("Heatmap Bucket", "heatmap_bucket")}
                  <select
                    value={String(heatmapBucket)}
                    onChange={(e) => setHeatmapBucket(Number(e.target.value) || 60)}
                    className={`${FIELD_CLASS} w-full`}
                  >
                    <option value="60">60 minutes</option>
                    <option value="30">30 minutes</option>
                  </select>
                </div>
              </div>

              {dateRangeError && (
                <div className="rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-rose-700 text-sm md:text-base">
                  {dateRangeError}
                </div>
              )}

              <div className="flex flex-wrap items-center gap-3">
                <div className="text-xs text-slate-500">Salon timezone: {tzName}</div>
                <div className="ml-auto flex flex-wrap items-center gap-3">
                  <button
                    type="button"
                    onClick={() => fetchReport(payloadBase)}
                    className={`${BTN_PRIMARY} min-w-[140px]`}
                    disabled={loading}
                  >
                    {loading ? "Loading..." : "Run Report"}
                  </button>
                  <button
                    id="sales-tickets-export"
                    type="button"
                    onClick={handleExport}
                    className={`${BTN_OUTLINE} min-w-[140px] ${getSectionClassName(
                      "sales-tickets-export"
                    )}`}
                    disabled={loading}
                  >
                    Export ZIP
                  </button>
                  <button
                    type="button"
                    onClick={() =>
                      navigate(getReportsHomePathForRoute(location.pathname))
                    }
                    className={`${BTN_OUTLINE} min-w-[140px]`}
                  >
                    Back to Reports
                  </button>
                </div>
              </div>
            </div>

            <div
              id="sales-tickets-kpis"
              className={`grid grid-cols-1 md:grid-cols-3 xl:grid-cols-6 gap-4 ${getSectionClassName(
                "sales-tickets-kpis"
              )}`}
            >
              {tiles.map((tile) => (
                <div
                  key={tile.label}
                  className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm"
                >
                  <div className="text-xs uppercase tracking-wide text-slate-500">
                    {tile.label}
                  </div>
                  <div className="mt-2 text-xl font-semibold text-slate-900">
                    {tile.value}
                  </div>
                </div>
              ))}
            </div>

            <div className="space-y-6">
              <div className="space-y-3">
                <SectionHeading label="Top N Rankings (by revenue)" />
                <div className="rounded-2xl border border-slate-200 bg-white shadow-sm p-5 space-y-4">
                  {rankingError && (
                    <div className="rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-rose-700 text-sm md:text-base">
                      {rankingError}
                    </div>
                  )}

              <div
                id="sales-tickets-details"
                className={`grid grid-cols-1 md:grid-cols-3 gap-4 ${getSectionClassName(
                  "sales-tickets-details"
                )}`}
              >
                    <div>
                      {renderFieldLabel("Ranking Type")}
                      <select
                        value={rankingType}
                        onChange={(e) => setRankingType(e.target.value)}
                        className={`${FIELD_CLASS} w-full`}
                      >
                        <option value="day_of_week">Day of week</option>
                        <option value="time_of_day">Time of day</option>
                        <option value="week_of_month">Week of month</option>
                        <option value="week_of_year">Week of year</option>
                        <option value="month_of_year">Month of year</option>
                      </select>
                    </div>
                    <div>
                      {renderFieldLabel("Top N")}
                      <input
                        type="number"
                        min="1"
                        max="50"
                        value={rankingTopN}
                        onChange={(e) => setRankingTopN(e.target.value)}
                        className={`${FIELD_CLASS} w-full`}
                      />
                    </div>
                    <div className="flex flex-col justify-end">
                      {rankingType === "time_of_day" && (
                        <div className="text-xs text-slate-500">
                          Using {heatmapBucket} minute buckets
                        </div>
                      )}
                      {rankingTotals && (
                        <div className="mt-2 text-sm text-slate-600">
                          Total revenue: {formatNumber(rankingTotals.total_revenue, 2)}
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="flex flex-wrap items-center gap-3">
                    <button
                      type="button"
                      onClick={fetchRanking}
                      className={`${BTN_PRIMARY} min-w-[140px]`}
                      disabled={rankingLoading || !!dateRangeError}
                    >
                      {rankingLoading ? "Loading..." : "Run Ranking"}
                    </button>
                  </div>
                </div>

                <ReportTable
                  columns={RANKING_COLUMNS}
                  rows={rankingRows}
                  rowKey="label"
                  loading={rankingLoading}
                  emptyMessage="No ranking data in this range."
                />
              </div>

              <div className="space-y-3">
                <SectionHeading
                  label="Ticket Volume Trends"
                  tooltip={SALES_AND_TICKETS_TOOLTIPS.section_ticket_volume}
                />
                <ReportTable
                  columns={TICKET_VOLUME_COLUMNS}
                  rows={ticketVolume}
                  rowKey="period"
                  loading={loading}
                  emptyMessage="No ticket volume data in this range."
                />
              </div>

              <div className="space-y-3">
                <SectionHeading
                  label="Ticket Type Breakdown"
                  tooltip={SALES_AND_TICKETS_TOOLTIPS.section_ticket_types}
                />
                <ReportTable
                  columns={TICKET_TYPE_COLUMNS}
                  rows={ticketTypeBreakdown}
                  rowKey="type"
                  loading={loading}
                  emptyMessage="No ticket mix data in this range."
                />
              </div>

              <div className="space-y-3">
                <SectionHeading
                  label="Cancellations & No-Shows"
                  tooltip={SALES_AND_TICKETS_TOOLTIPS.section_cancellations}
                />
                <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
                  <ReportTable
                    columns={CANCELLATION_COLUMNS}
                    rows={cancellations}
                    rowKey="period"
                    loading={loading}
                    emptyMessage="No cancellation data in this range."
                  />
                  <ReportTable
                    columns={CANCELLATION_TIMING_COLUMNS}
                    rows={cancellationTiming}
                    rowKey="bucket"
                    loading={loading}
                    emptyMessage="No cancellation timing data."
                  />
                </div>
              </div>

              <div className="space-y-3">
                <SectionHeading
                  label="Booking Source / Channel"
                  tooltip={SALES_AND_TICKETS_TOOLTIPS.section_booking_sources}
                />
                <ReportTable
                  columns={SOURCE_COLUMNS}
                  rows={bookingSources}
                  rowKey="source"
                  loading={loading}
                  emptyMessage="No booking source data in this range."
                />
              </div>

              <div className="space-y-3">
                <SectionHeading
                  label="Peak Hours Heatmap"
                  tooltip={SALES_AND_TICKETS_TOOLTIPS.section_heatmap}
                />
                <ReportTable
                  columns={heatmapColumns}
                  rows={heatmapRows}
                  rowKey="weekday"
                  loading={loading}
                  emptyMessage="No peak hour data in this range."
                />
              </div>

              <div className="space-y-3">
                <SectionHeading
                  label="Visit-to-Sale Conversion"
                  tooltip={SALES_AND_TICKETS_TOOLTIPS.section_conversion}
                />
                <ReportTable
                  columns={CONVERSION_COLUMNS}
                  rows={visitToSale}
                  rowKey="period"
                  loading={loading}
                  emptyMessage="No conversion data in this range."
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
